import { useWorkTab } from '@/app/work-tabs/work-tab-context';
import { Link } from 'react-router-dom';
import {
  formatCustomFieldValue,
  type CustomFieldDefinition,
  type ProductListItem,
} from '@erp/shared';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type VisibilityState,
  type Updater,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { customFieldLabel } from '@/features/custom-fields';
import { productImageUrl } from '@/features/products/api/use-product-images';
import { ProductImageView } from './product-image-view';
import { DataCard, DataCardList } from '@/components/common/data-card';
import { formatMoney, formatQuantity } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const VISIBILITY_STORAGE_KEY = 'erp-products-columns';
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  articleNumber: 'articleNumber',
  name: 'name',
  productGroup: 'productGroup',
  stock: 'stockQuantity',
  listPrice: 'listPrice',
  status: 'status',
};

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  ACTIVE: 'success',
  PHASE_OUT: 'warning',
  DISCONTINUED: 'outline',
  BLOCKED: 'danger',
};

function readVisibility(): VisibilityState {
  const raw = window.localStorage.getItem(VISIBILITY_STORAGE_KEY);
  if (raw === null) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as VisibilityState) : {};
  } catch {
    return {};
  }
}

function renderCustomFieldValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? '✓' : '—';
  }
  return String(value);
}

export interface ProductTableProps {
  savedColumns?: { id: string; visible: boolean }[];
  onSavedColumnsChange?: (columns: { id: string; visible: boolean }[]) => void;
  rows: ProductListItem[];
  definitions: CustomFieldDefinition[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  sort: string;
  onSortChange: (sort: string) => void;
  onOpen: (id: string) => void;
  onCreate: () => void;
  hasFilters: boolean;
  canCreate: boolean;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export function ProductTable({
  savedColumns,
  onSavedColumnsChange,
  rows,
  definitions,
  isLoading,
  isError,
  onRetry,
  sort,
  onSortChange,
  onOpen,
  onCreate,
  hasFilters,
  canCreate,
  selectedIds,
  onSelectionChange,
}: ProductTableProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const workTab = useWorkTab();
  const [localVisibility, setLocalVisibility] = useState<VisibilityState>(readVisibility);
  const columnVisibility = savedColumns
    ? Object.fromEntries(savedColumns.map((c) => [c.id, c.visible]))
    : localVisibility;
  const setColumnVisibility = (updater: Updater<VisibilityState>): void => {
    const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
    if (savedColumns && onSavedColumnsChange)
      onSavedColumnsChange(savedColumns.map((c) => ({ ...c, visible: next[c.id] !== false })));
    else setLocalVisibility(next);
  };
  const [activeIndex, setActiveIndex] = useState(0);
  const bodyRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    window.localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(localVisibility));
  }, [localVisibility]);

  const listColumns = useMemo(
    () =>
      definitions.filter((definition) => definition.showInList && definition.archivedAt === null),
    [definitions],
  );

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

  const columns = useMemo<ColumnDef<ProductListItem>[]>(() => {
    const base: ColumnDef<ProductListItem>[] = [
      {
        id: 'select',
        header: () => (
          <Checkbox
            checked={allSelected}
            aria-label={t('products.selection.toggleAll')}
            onCheckedChange={(checked) =>
              onSelectionChange(checked === true ? rows.map((row) => row.id) : [])
            }
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selected.has(row.original.id)}
            aria-label={t('products.selection.toggleRow', { name: row.original.name })}
            onClick={(event) => event.stopPropagation()}
            onCheckedChange={() =>
              onSelectionChange(
                selected.has(row.original.id)
                  ? selectedIds.filter((entry) => entry !== row.original.id)
                  : [...selectedIds, row.original.id],
              )
            }
          />
        ),
      },
      {
        id: 'image',
        header: () => <span className="sr-only">{t('products.columns.image')}</span>,
        cell: ({ row }) => {
          const image = row.original.primaryImage;
          return (
            <ProductImageView
              src={
                image === null ? undefined : productImageUrl(row.original.id, image.id, 'thumbnail')
              }
              alt={image?.altText || row.original.name}
              className="h-12 w-12"
              sizes="48px"
            />
          );
        },
      },
      {
        id: 'articleNumber',
        accessorKey: 'articleNumber',
        header: t('products.columns.articleNumber'),
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.articleNumber}</span>,
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: t('products.columns.name'),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <Link
              to={`/products/${row.original.id}`}
              onClick={(event) => event.stopPropagation()}
              className="font-medium underline-offset-4 hover:underline"
            >
              {row.original.name}
            </Link>
            {row.original.ean === null ? null : (
              <span className="font-mono text-xs text-muted-foreground">{row.original.ean}</span>
            )}
          </div>
        ),
      },
      {
        id: 'productGroup',
        accessorKey: 'productGroup',
        header: t('products.columns.productGroup'),
        cell: ({ row }) => row.original.productGroup ?? '—',
      },
      {
        id: 'stock',
        accessorKey: 'stockQuantity',
        header: t('products.columns.stock'),
        cell: ({ row }) =>
          row.original.hasMigrationSource ? (
            <span className="text-sm text-muted-foreground">{t('catalogReadiness.DEFERRED')}</span>
          ) : (
            <span
              className={cn(
                'tabular-nums',
                !row.original.hasMigrationSource &&
                  row.original.isBelowMinStock &&
                  'font-semibold text-destructive',
              )}
            >
              {formatQuantity(row.original.stockQuantity, i18n.language)}{' '}
              <span className="text-xs text-muted-foreground">
                {t(`products.units.${row.original.baseUnit}`, {
                  defaultValue: row.original.baseUnit,
                })}
              </span>
            </span>
          ),
      },
      {
        id: 'listPrice',
        accessorKey: 'listPrice',
        header: t('products.columns.listPrice'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatMoney(row.original.listPrice, row.original.currency, i18n.language)}
          </span>
        ),
      },
      {
        id: 'batches',
        header: t('products.columns.batches'),
        cell: ({ row }) => {
          if (!row.original.isBatchManaged) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <span className="flex flex-wrap items-center gap-1">
              <Badge variant="outline">
                {t('products.batchBadge.count', { count: row.original.batchCount })}
              </Badge>
              {row.original.expiringBatchCount > 0 ? (
                <Badge variant="danger">
                  {t('products.batchBadge.expiring', { count: row.original.expiringBatchCount })}
                </Badge>
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('products.columns.status'),
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANTS[row.original.status] ?? 'secondary'}>
            {t(`products.statuses.${row.original.status}`)}
          </Badge>
        ),
      },
      {
        id: 'purchasePrice',
        accessorKey: 'purchasePrice',
        header: t('products.columns.purchasePrice'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatMoney(row.original.purchasePrice, row.original.currency, i18n.language)}
          </span>
        ),
      },
      {
        id: 'available',
        accessorKey: 'availableQuantity',
        header: t('products.columns.available'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.hasMigrationSource
              ? t('catalogReadiness.DEFERRED')
              : formatQuantity(row.original.availableQuantity, i18n.language)}
          </span>
        ),
      },
      {
        id: 'tags',
        accessorKey: 'tags',
        header: t('products.columns.tags'),
        cell: ({ row }) =>
          row.original.tags.length === 0 ? (
            '—'
          ) : (
            <span className="flex flex-wrap gap-1">
              {row.original.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </span>
          ),
      },
    ];

    const dynamic: ColumnDef<ProductListItem>[] = listColumns.map((definition) => ({
      id: `cf-${definition.id}`,
      header: customFieldLabel(definition, i18n.language),
      cell: ({ row }) =>
        definition.fieldType === 'WEEKLY_SCHEDULE'
          ? formatCustomFieldValue(
              definition,
              row.original.customFields[definition.key],
              i18n.language,
            ) || '—'
          : renderCustomFieldValue(row.original.customFields[definition.key]),
    }));

    return [...base, ...dynamic];
  }, [t, i18n.language, listColumns, rows, selected, allSelected, selectedIds, onSelectionChange]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnVisibility, columnOrder: savedColumns?.map((c) => c.id) ?? [] },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  useEffect(() => {
    setActiveIndex(0);
  }, [rows]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!workTab.active) return;
      const target = event.target as HTMLElement | null;
      const isInteractive = target?.closest(
        'input, textarea, select, button, a, summary, [contenteditable="true"], [role="combobox"], [role="menu"], [role="listbox"], [role="dialog"], [role="alertdialog"]',
      );
      if (
        event.defaultPrevented ||
        isInteractive ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        rows.length === 0
      ) {
        return;
      }

      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, rows.length - 1));
        return;
      }
      if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        return;
      }
      if (event.key === 'Enter') {
        const row = rows[activeIndex];
        if (row !== undefined) {
          event.preventDefault();
          onOpen(row.id);
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rows, activeIndex, onOpen, workTab.active]);

  useEffect(() => {
    const active = bodyRef.current?.querySelector<HTMLTableRowElement>('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const [sortField, sortOrder] = sort.split(':');

  const toggleSort = (columnId: string): void => {
    const field = SORT_FIELD_BY_COLUMN[columnId];
    if (field === undefined) {
      return;
    }
    const nextOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange(`${field}:${nextOrder}`);
  };

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-md border border-border p-6">
        <p className="text-sm text-muted-foreground">{t('products.states.error')}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Columns3 className="h-4 w-4" aria-hidden="true" />
              {t('products.columnsMenu')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
            <DropdownMenuLabel>{t('products.columnsMenu')}</DropdownMenuLabel>
            {table
              .getAllLeafColumns()
              .filter((column) => column.id !== 'select')
              .map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={column.getIsVisible()}
                  onCheckedChange={(checked) => column.toggleVisibility(checked === true)}
                >
                  {typeof column.columnDef.header === 'string'
                    ? column.columnDef.header
                    : column.id === 'image'
                      ? t('products.columns.image')
                      : column.id}
                </DropdownMenuCheckboxItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Ab `md` Tabelle, darunter Kartenliste — siehe Responsive-Regeln. */}
      <div className="hidden rounded-md border border-border md:block">
        {isLoading ? (
          <div className="flex flex-col gap-2 p-4" aria-busy="true">
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <p className="text-sm text-muted-foreground">
              {hasFilters ? t('products.states.emptyFiltered') : t('products.states.empty')}
            </p>
            {canCreate ? (
              <Button size="sm" onClick={onCreate}>
                {t('products.actions.create')}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="max-h-[65vh] overflow-auto">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const field = SORT_FIELD_BY_COLUMN[header.column.id];
                      const isSorted = field !== undefined && field === sortField;
                      return (
                        <TableHead
                          key={header.id}
                          aria-sort={
                            isSorted ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'
                          }
                        >
                          {field === undefined ? (
                            flexRender(header.column.columnDef.header, header.getContext())
                          ) : (
                            <button
                              type="button"
                              className="flex items-center gap-1 hover:text-foreground"
                              onClick={() => toggleSort(header.column.id)}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {isSorted ? (
                                sortOrder === 'asc' ? (
                                  <ArrowUp className="h-3 w-3" aria-hidden="true" />
                                ) : (
                                  <ArrowDown className="h-3 w-3" aria-hidden="true" />
                                )
                              ) : (
                                <ChevronsUpDown className="h-3 w-3 opacity-50" aria-hidden="true" />
                              )}
                            </button>
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody ref={bodyRef}>
                {table.getRowModel().rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    data-active={index === activeIndex}
                    tabIndex={0}
                    className={cn(
                      'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      index === activeIndex && 'bg-accent/60',
                    )}
                    onClick={() => onOpen(row.original.id)}
                    onFocus={() => setActiveIndex(index)}
                    onKeyDown={(event) => {
                      if (
                        (event.key === 'Enter' || event.key === ' ') &&
                        event.target === event.currentTarget
                      ) {
                        event.preventDefault();
                        onOpen(row.original.id);
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2 md:hidden" aria-busy="true">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-border p-10 text-center md:hidden">
          <p className="text-sm text-muted-foreground">
            {hasFilters ? t('products.states.emptyFiltered') : t('products.states.empty')}
          </p>
          {canCreate ? (
            <Button size="sm" onClick={onCreate}>
              {t('products.actions.create')}
            </Button>
          ) : null}
        </div>
      ) : (
        <DataCardList>
          {rows.map((row) => (
            <DataCard
              key={row.id}
              title={row.name}
              subtitle={row.articleNumber}
              badge={
                !row.hasMigrationSource && row.isBelowMinStock ? (
                  <Badge variant="warning">{t('products.badges.belowMinStock')}</Badge>
                ) : undefined
              }
              metric={
                columnVisibility['listPrice'] === false
                  ? undefined
                  : formatMoney(row.listPrice, row.currency, i18n.language)
              }
              expandLabel={t('common.showDetails')}
              collapseLabel={t('common.hideDetails')}
              onOpen={() => onOpen(row.id)}
              media={
                columnVisibility['image'] === false ? undefined : (
                  <ProductImageView
                    src={
                      row.primaryImage === null
                        ? undefined
                        : productImageUrl(row.id, row.primaryImage.id, 'list')
                    }
                    alt={row.primaryImage?.altText || row.name}
                    className="h-16 w-16"
                    sizes="64px"
                  />
                )
              }
              details={
                table
                  .getRowModel()
                  .rows.find((entry) => entry.original.id === row.id)
                  ?.getVisibleCells()
                  .filter(
                    (cell) =>
                      !['select', 'image', 'name', 'articleNumber', 'listPrice'].includes(
                        cell.column.id,
                      ),
                  )
                  .map((cell) => ({
                    label:
                      typeof cell.column.columnDef.header === 'string'
                        ? cell.column.columnDef.header
                        : cell.column.id,
                    value: flexRender(cell.column.columnDef.cell, cell.getContext()),
                  })) ?? []
              }
            />
          ))}
        </DataCardList>
      )}
    </div>
  );
}
